import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/home';
import { categoryComics } from '@/mocks/category';

type SortKey = 'latest' | 'popular' | 'rating';

const categoryData: Record<string, { title: string; description: string; heroImage: string }> = {
  fantasy: {
    title: '奇幻冒险',
    description: '探索充满魔法与奇幻元素的精彩世界，跟随主角们踏上惊心动魄的冒险旅程。从东方玄幻到西方魔幻，这里汇集了最优秀的奇幻冒险漫画作品。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20fantasy%20adventure%20manga%20panoramic%20illustration%2C%20magical%20world%20with%20floating%20islands%20and%20mythical%20creatures%2C%20warm%20golden%20lighting%2C%20epic%20scale%20composition%2C%20dreamy%20and%20whimsical%20atmosphere%2C%20clean%20artistic%20style&width=1600&height=600&seq=cat-hero-fantasy-01&orientation=landscape',
  },
  xianxia: {
    title: '古风仙侠',
    description: '修仙问道，仗剑天涯。在仙侠的世界里，见证凡人逆天改命、修炼成仙的传奇故事。古风画风与仙侠世界观完美融合。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20xianxia%20cultivation%20world%20panoramic%20illustration%2C%20ancient%20mountain%20peaks%20shrouded%20in%20mist%2C%20flying%20swords%20and%20immortal%20palaces%2C%20traditional%20Chinese%20ink%20painting%20style%20with%20golden%20accents%2C%20serene%20and%20majestic%20atmosphere&width=1600&height=600&seq=cat-hero-xianxia-01&orientation=landscape',
  },
  action: {
    title: '热血战斗',
    description: '燃爆的战斗场面、扣人心弦的对决！从武道巅峰到超能力大战，每一页都让你肾上腺素飙升。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20action%20battle%20manga%20panoramic%20illustration%2C%20epic%20martial%20arts%20combat%20with%20dynamic%20energy%20effects%20and%20dramatic%20lighting%2C%20warriors%20in%20intense%20fighting%20poses%2C%20powerful%20and%20explosive%20atmosphere%2C%20bold%20illustration%20style&width=1600&height=600&seq=cat-hero-action-01&orientation=landscape',
  },
  mystery: {
    title: '悬疑推理',
    description: '步步惊心的悬疑剧情，烧脑的推理过程。每一个细节都可能是破案的关键，跟随主角揭开层层谜团。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20mystery%20thriller%20manga%20panoramic%20illustration%2C%20dark%20rainy%20alley%20with%20mysterious%20shadows%20and%20subtle%20clues%20scattered%2C%20atmospheric%20noir%20lighting%20with%20cool%20tones%2C%20suspenseful%20and%20intriguing%20composition%2C%20clean%20artistic%20style&width=1600&height=600&seq=cat-hero-mystery-01&orientation=landscape',
  },
  scifi: {
    title: '科幻未来',
    description: '探索宇宙奥秘，畅想未来科技。从星际旅行到人工智能，这里有你想象不到的科幻世界。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20sci-fi%20futuristic%20manga%20panoramic%20illustration%2C%20massive%20space%20station%20orbiting%20distant%20planet%20with%20colorful%20nebula%2C%20advanced%20technology%20and%20sleek%20design%2C%20awe-inspiring%20cosmic%20atmosphere%2C%20clean%20modern%20illustration%20style&width=1600&height=600&seq=cat-hero-scifi-01&orientation=landscape',
  },
  comedy: {
    title: '搞笑日常',
    description: '笑到停不下来！轻松搞笑的日常故事，夸张的表情和爆笑的剧情，让你忘记一切烦恼。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20comedy%20manga%20panoramic%20illustration%2C%20colorful%20and%20playful%20scene%20with%20funny%20characters%20in%20exaggerated%20situations%2C%20bright%20cheerful%20warm%20colors%2C%20energetic%20and%20humorous%20atmosphere%2C%20lively%20cartoon%20illustration%20style&width=1600&height=600&seq=cat-hero-comedy-01&orientation=landscape',
  },
  'slice-of-life': {
    title: '治愈日常',
    description: '温暖治愈的日常故事，细腻的情感描绘。在平凡的生活中发现不平凡的美好瞬间。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20slice%20of%20life%20manga%20panoramic%20illustration%2C%20peaceful%20neighborhood%20street%20with%20cherry%20blossoms%20falling%2C%20warm%20gentle%20sunlight%20creating%20soft%20shadows%2C%20heartwarming%20and%20calming%20atmosphere%2C%20soft%20watercolor%20illustration%20style&width=1600&height=600&seq=cat-hero-slice-01&orientation=landscape',
  },
  horror: {
    title: '惊悚恐怖',
    description: '胆量挑战！精心设计的恐怖氛围和细思极恐的剧情，让你在安全的屏幕前也能感受到脊背发凉的刺激。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20horror%20manga%20panoramic%20illustration%2C%20eerie%20abandoned%20mansion%20at%20night%20with%20faint%20ghostly%20figures%20in%20windows%2C%20cold%20blue%20moonlight%20casting%20long%20shadows%2C%20chilling%20and%20suspenseful%20atmosphere%2C%20dark%20atmospheric%20illustration%20style&width=1600&height=600&seq=cat-hero-horror-01&orientation=landscape',
  },
  romance: {
    title: '恋爱甜宠',
    description: '甜到掉牙的恋爱故事！从校园初恋到都市情缘，每一段恋情都让人心动不已。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20romance%20manga%20panoramic%20illustration%2C%20couple%20walking%20hand%20in%20hand%20through%20flower%20field%20at%20sunset%2C%20warm%20golden%20pink%20sky%2C%20dreamy%20romantic%20atmosphere%20with%20floating%20flower%20petals%2C%20soft%20gentle%20illustration%20style&width=1600&height=600&seq=cat-hero-romance-01&orientation=landscape',
  },
  school: {
    title: '校园青春',
    description: '重返青春校园，感受最纯粹的友谊和梦想。热血运动、青涩初恋、奋斗备考...你的青春记忆都在这里。',
    heroImage: 'https://readdy.ai/api/search-image?query=Chinese%20school%20life%20manga%20panoramic%20illustration%2C%20vibrant%20high%20school%20campus%20with%20students%20enjoying%20club%20activities%2C%20cherry%20blossom%20trees%20in%20full%20bloom%2C%20bright%20energetic%20spring%20atmosphere%2C%20youthful%20and%20lively%20illustration%20style&width=1600&height=600&seq=cat-hero-school-01&orientation=landscape',
  },
};

export default function CategoryPage() {
  const { slug = 'fantasy' } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<SortKey>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const catInfo = categoryData[slug] || categoryData.fantasy;

  const filteredComics = useMemo(() => {
    let list = categoryComics.filter((c) => c.categorySlug === slug);
    if (sortBy === 'popular') {
      list = [...list].sort((a, b) => {
        const va = parseInt(a.views.replace('万', '0000'), 10);
        const vb = parseInt(b.views.replace('万', '0000'), 10);
        return vb - va;
      });
    } else if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const pagedComics = filteredComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
          <img
            src={catInfo.heroImage}
            alt={catInfo.title}
            title={`${catInfo.title}漫画 - 布丁漫画网`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-10 md:pb-14">
            <div className="max-w-[1280px] mx-auto">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-2">{catInfo.title}</h1>
              <p className="text-sm md:text-base text-background-50/75 max-w-xl">{catInfo.description}</p>
            </div>
          </div>
        </section>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: t('category:breadcrumb_home'), path: '/' },
              { label: t('category:breadcrumb_category') },
              { label: catInfo.title },
            ]}
          />
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14 md:pb-20">
          <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1 overflow-x-auto mb-6">
            {categories.slice(0, 10).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8">
            <p className="text-sm text-foreground-500">
              {t('category:result_count', { count: filteredComics.length })}
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { key: 'latest', label: t('category:sort_latest') },
                { key: 'popular', label: t('category:sort_popular') },
                { key: 'rating', label: t('category:sort_rating') },
              ] as { key: SortKey; label: string }[]).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => {
                    setSortBy(opt.key);
                    setCurrentPage(1);
                  }}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-accent-500 text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pagedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {pagedComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-accent-300 transition-all duration-200 cursor-pointer"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - ${comic.author}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                    />
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 truncate">{comic.title}</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                    <div className="flex items-center justify-between mt-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-accent-600 font-medium">{comic.rating}{t('category:comic_rating')}</span>
                        <span className="text-[10px] text-foreground-400">{comic.chapters}{t('category:comic_chapters')}</span>
                      </div>
                      {comic.isNew && (
                        <span className="inline-flex px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-medium">
                          NEW
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">{t('category:no_results')}</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-10">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-9 h-9 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    page === currentPage
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `${catInfo.title}漫画 - 布丁漫画网`,
              description: catInfo.description,
              url: `http://591chaxin.cn/category/${slug}`,
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'http://591chaxin.cn/' },
                  { '@type': 'ListItem', position: 2, name: '分类', item: 'http://591chaxin.cn/category/fantasy' },
                  { '@type': 'ListItem', position: 3, name: catInfo.title },
                ],
              },
              numberOfItems: filteredComics.length,
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}